import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Dialog } from "../../../../ui/components/Dialog";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import { Select } from "../../../../ui/components/Select";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { FeatherInfo, FeatherX } from "@subframe/core";
import {
  Tax,
  createSemanticOverride,
  SemanticOverrideRequest,
} from "../../../../services/api";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { AccountNameDropdown } from "../../../../shared/presentation/components";
import { GLMappingEntity } from "../../../../shared/domain/entities/GLMappingEntity";

// Permissions
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import { ChangeDetails } from "../../../../shared/utils/reviewWorkflow";

interface EditLineItemForInvoiceComponentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingLineItemData: any | null;
  originalAccountName: string;
  originalAccountId: string;
  editingLineItemIndex: number | null;
  taxes: Tax[];
  taxesLoading?: boolean;
  customerName?: string;
  customerId?: string;
  isNewItem?: boolean;
  isGstEnabled?: boolean;
  currencySymbol?: string;
  gstTreatment?: string;
  invoiceId?: string | number | null; // Invoice document ID for review workflow
  onSave: () => void;
  onCancel: () => void;
  onLineItemDataChange: (data: any) => void;
  onGLOverrideNeedsReview?: (glOverrideData: {
    lineItemIndex: number;
    glData: {
      old_account_id: string;
      old_account_name: string;
      new_account_id: string;
      new_account_name: string;
    };
    changeScope: "always" | "once";
    reason: string;
    overrideData: SemanticOverrideRequest;
  }) => void;
}

const EditLineItemForInvoiceComponent: React.FC<
  EditLineItemForInvoiceComponentProps
> = ({
  open,
  onOpenChange,
  editingLineItemData,
  editingLineItemIndex,
  originalAccountName,
  originalAccountId,
  taxes,
  taxesLoading = false,
  customerName,
  customerId,
  isNewItem = false,
  isGstEnabled = true,
  currencySymbol = "₹",
  gstTreatment,
  invoiceId = null,
  onSave,
  onCancel,
  onLineItemDataChange,
  onGLOverrideNeedsReview,
}) => {
  // Helper method for rounding
  const roundOff = (value: number): string => {
    // return Math.round(value).toString();
    return value.toFixed(2);
  };

  // Account change state
  const [accountChangeReason, setAccountChangeReason] = useState("");
  const [changeScope, setChangeScope] = useState<"once" | "always" | null>(
    null,
  );
  const [showAccountChangeSection, setShowAccountChangeSection] =
    useState(false);
  const [storedOriginalAccountName, setStoredOriginalAccountName] =
    useState("");
  const [storedOriginalAccountId, setStoredOriginalAccountId] = useState("");

  // Selected account state for efficient handleSemanticOverride
  const [selectedAccount, setSelectedAccount] =
    useState<GLMappingEntity | null>(null);

  // API state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Permissions - access directly from context
  const {
    permissions,
    loading: permissionsLoading,
    refreshPermissions,
  } = usePermissions();

  // Use ref to prevent multiple fetch attempts per modal open
  const fetchAttemptedRef = useRef(false);

  // Refs for number inputs - prevent scroll-to-change (React's onWheel is passive, so we use addEventListener)
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const rateInputRef = useRef<HTMLInputElement>(null);

  // Reset ref when modal closes so we can fetch again next time it opens
  useEffect(() => {
    if (!open) {
      fetchAttemptedRef.current = false;
    }
  }, [open]);

  // Prevent scroll from changing number input values (must use addEventListener with passive: false)
  useEffect(() => {
    if (!open || !editingLineItemData) return;
    const quantityEl = quantityInputRef.current;
    const rateEl = rateInputRef.current;
    const handler = (e: WheelEvent) => e.preventDefault();
    if (quantityEl) {
      quantityEl.addEventListener("wheel", handler, { passive: false });
    }
    if (rateEl) {
      rateEl.addEventListener("wheel", handler, { passive: false });
    }
    return () => {
      quantityEl?.removeEventListener("wheel", handler);
      rateEl?.removeEventListener("wheel", handler);
    };
  }, [open, editingLineItemData]);

  // Fetch permissions when modal opens (only once per modal open session)
  useEffect(() => {
    if (
      open &&
      !permissions &&
      !permissionsLoading &&
      !fetchAttemptedRef.current
    ) {
      fetchAttemptedRef.current = true;
      refreshPermissions();
    }
    // Include dependencies to read fresh values, but ref prevents re-fetching
  }, [open, permissions, permissionsLoading, refreshPermissions]);

  // Compute permission info directly from permissions object
  // Will automatically update when permissions load (permissions changes from null to object)
  const overridePermInfo = useMemo(() => {
    if (!permissions) {
      return { granted: false, requiresReview: false };
    }
    const perm =
      permissions[MODULE_CODES.INVOICE]?.[ACTION_CODES.OVERRIDE_GL_PERMANENT];
    const granted = perm?.granted ?? false;
    const requiresReview = granted && (perm?.requires_review ?? false);
    return { granted, requiresReview };
  }, [permissions]);

  const canOverridePerm = overridePermInfo.granted;
  const needsReview = overridePermInfo.requiresReview;

  const overrideOnceInfo = useMemo(() => {
    if (!permissions) {
      return { granted: false, requiresReview: false };
    }
    const perm =
      permissions[MODULE_CODES.INVOICE]?.[ACTION_CODES.OVERRIDE_GL_ONCE];
    const granted = perm?.granted ?? false;
    const requiresReview = granted && (perm?.requires_review ?? false);
    return { granted, requiresReview };
  }, [permissions]);

  const canOverrideOnce = overrideOnceInfo.granted;

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingOverrideAction, setPendingOverrideAction] = useState<{
    action: () => void;
    changeDetails?: ChangeDetails;
  } | null>(null);

  // Check for approved review if needed
  // const [hasApprovedReview, setHasApprovedReview] = useState<boolean | null>(
  //   null
  // );

  // // Note: We no longer check for approved reviews on mount
  // // Instead, we compare accounts directly when user tries to override
  // // This ensures each new account change requires a review, even if a previous override was approved
  // useEffect(() => {
  //   // Set hasApprovedReview based on whether review is needed
  //   if (changeScope !== "always" || !needsReview) {
  //     setHasApprovedReview(true); // No review needed for "once" or if review not required
  //   } else {
  //     // For "always" with review required, we'll check account changes at override time
  //     setHasApprovedReview(null);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [changeScope, needsReview]);

  // Tax-inclusive rate toggle
  const [rateIncludesTax, setRateIncludesTax] = useState(false);
  const [inclusiveRateSnapshot, setInclusiveRateSnapshot] = useState<
    number | null
  >(null);
  const isOverseas = gstTreatment === "overseas";

  const roundToTwo = (value: number) => parseFloat((value || 0).toFixed(2));

  // Set original account name/id when modal opens
  useEffect(() => {
    if (open) {
      setStoredOriginalAccountName(originalAccountName);
      setStoredOriginalAccountId(originalAccountId);
      setShowAccountChangeSection(false);
      setAccountChangeReason("");
      setChangeScope(null);
      setError(null);
      setRateIncludesTax(false);
      setInclusiveRateSnapshot(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, originalAccountName, originalAccountId, gstTreatment]);

  // Handle semantic override API call
  const handleSemanticOverride = async () => {
    if (!editingLineItemData || !accountChangeReason.trim()) {
      setError("Please provide a reason for the account change");
      return;
    }
    if (!selectedAccount) {
      setError("Selected account not found");
      return;
    }

    // "Change it once": never create a permanent semantic override.
    // The GL change is persisted on the line item itself via onSave; the API
    // (which creates a *permanent* mapping) must not be called.
    if (changeScope === "once") {
      if (!canOverrideOnce) {
        setError("You do not have permission to override GL accounts");
        setIsLoading(false);
        return;
      }
      onSave();
      return;
    }

    // "Change it always" from here onwards.
    if (!canOverridePerm) {
      setError(
        "You do not have permission to permanently override GL accounts",
      );
      setIsLoading(false);
      return;
    }

    const ogAccountName =
      storedOriginalAccountName || originalAccountName || "";
    const ogAccountId = storedOriginalAccountId || originalAccountId || "";
    const newAccountName = selectedAccount.gl_name || "";
    const newAccountId = selectedAccount.account_id || "";
    const accountHasChanged = ogAccountId !== newAccountId;

    // Account didn't actually change - don't create a no-op override.
    if (!accountHasChanged) {
      onSave();
      return;
    }

    // Review required: defer the API call; let the parent queue this for the
    // review/approval flow.
    if (needsReview && invoiceId) {
      if (onGLOverrideNeedsReview && editingLineItemIndex !== null) {
        const overrideData: SemanticOverrideRequest = {
          line_item: {
            vendor: "",
            vendor_id: "",
            description: editingLineItemData.description || "",
            product_description: editingLineItemData.description || "",
            hsn: editingLineItemData.hsn_or_sac || "",
            hsn_description: editingLineItemData.hsn_description || "",
            customer: customerName || "",
            customer_id: customerId || "",
          },
          // gl_id: selectedAccount.gl_id,
          gl_id: selectedAccount.gl_name,
          similarity_threshold: 0.85,
          document_type: "invoice",
          account_id: selectedAccount.account_id,
          note: accountChangeReason,
        };

        onGLOverrideNeedsReview({
          lineItemIndex: editingLineItemIndex,
          glData: {
            old_account_id: ogAccountId,
            old_account_name: ogAccountName,
            new_account_id: newAccountId,
            new_account_name: newAccountName,
          },
          changeScope: "always",
          reason: accountChangeReason,
          overrideData: overrideData,
        });
      }
      onSave();
      return;
    }

    // "always", no review required, account did change - hit the API now.
    await performSemanticOverride();
  };

  // Separate function to perform the actual semantic override
  const performSemanticOverride = async () => {
    if (!selectedAccount) {
      setError("Selected account not found");
      setIsLoading(false);
      return;
    }
    if (!editingLineItemData) {
      setError("Line item data not found");
      setIsLoading(false);
      return;
    }

    // Store in local variable for type narrowing
    const account = selectedAccount;
    const lineItemData = editingLineItemData;

    setIsLoading(true);
    setError(null);
    try {
      const overrideData: SemanticOverrideRequest = {
        line_item: {
          vendor: "",
          vendor_id: "",
          description: lineItemData.description || "",
          product_description: lineItemData.description || "",
          hsn: lineItemData.hsn_or_sac || "",
          hsn_description: lineItemData.hsn_description || "",
          customer: customerName || "",
          customer_id: customerId || "",
        },
        // gl_id: account.gl_id,
        gl_id: account.gl_name,
        similarity_threshold: 0.85,
        document_type: "invoice",
        account_id: account.account_id,
        note: accountChangeReason,
      };
      const response = await createSemanticOverride(overrideData);
      if (response.success) {
        // Continue with normal save
        onSave();
      } else {
        throw new Error("Please try again later");
      }
    } catch (error) {
      console.error("Error creating semantic override:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to create semantic override",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle account selection from AccountNameDropdown
  const handleAccountSelection = (account: GLMappingEntity) => {
    // Store full account entity in state for efficient handleSemanticOverride
    setSelectedAccount(account);

    const accountId = account.account_id;
    const updatedData = {
      ...editingLineItemData,
      account_name: account.gl_name,
      account_id: accountId,
    };
    onLineItemDataChange(updatedData);

    // For new items, show account change section as soon as they select an account
    if (isNewItem) {
      setShowAccountChangeSection(true);
    } else {
      // For existing items, show only if the account_id has changed
      const hasAccountChanged =
        storedOriginalAccountId &&
        storedOriginalAccountId !== account.account_id;

      if (hasAccountChanged) {
        setShowAccountChangeSection(true);
      } else {
        setShowAccountChangeSection(false);
      }
    }
  };

  // Handle tax selection from Select
  const handleTaxSelection = (selectedTaxId: string) => {
    // Handle "NO Tax" option for overseas customers
    if (selectedTaxId === "NO_TAX") {
      const quantity = editingLineItemData.quantity || 0;
      const rate: number = roundToTwo(Number(editingLineItemData.rate) || 0);
      const itemTotal = quantity * rate;
      const updatedData = {
        ...editingLineItemData,
        tax_id: "NO_TAX",
        tax_name: "",
        tax_percentage: 0,
        tax_amount: 0,
        item_total: itemTotal,
        item_total_inclusive_of_tax: itemTotal,
      };
      onLineItemDataChange(updatedData);
      return;
    }

    const selectedTax = taxes.find((tax) => tax.tax_id === selectedTaxId);
    if (selectedTax) {
      const taxPercentage = parseFloat(selectedTax.tax_percentage);
      const quantity = editingLineItemData.quantity || 0;
      let rate = roundToTwo(editingLineItemData.rate || 0);

      if (rateIncludesTax && inclusiveRateSnapshot !== null && isGstEnabled) {
        rate = roundToTwo(inclusiveRateSnapshot / (1 + taxPercentage / 100));
      }

      const itemTotal = quantity * rate;
      const taxAmount = (itemTotal * taxPercentage) / 100;

      // Ensure tax_name is set - use tax_name from tax object, or construct it if empty
      let taxName = selectedTax.tax_name;
      if (!taxName || taxName.trim() === "") {
        // Construct tax_name from tax_type and tax_percentage if tax_name is empty
        // Examples: IGST18, GST18, CGST9, SGST9
        const taxType = selectedTax.tax_type || "";
        const taxSpecificType = selectedTax.tax_specific_type || "";

        // If tax_id looks like a tax name (e.g., "IGST18"), use it
        if (selectedTax.tax_id && /^[A-Z]+(\d+)$/i.test(selectedTax.tax_id)) {
          taxName = selectedTax.tax_id.toUpperCase();
        } else if (taxSpecificType) {
          // Use tax_specific_type if available (e.g., "IGST", "CGST", "SGST")
          taxName = `${taxSpecificType.toUpperCase()}${Math.round(
            taxPercentage,
          )}`;
        } else if (taxType) {
          // Use tax_type as fallback
          taxName = `${taxType.toUpperCase()}${Math.round(taxPercentage)}`;
        } else {
          // Final fallback: use tax_id
          taxName = selectedTax.tax_id || "";
        }
      }

      const updatedData = {
        ...editingLineItemData,
        rate,
        tax_id: selectedTax.tax_id,
        tax_name: taxName,
        tax_percentage: taxPercentage,
        tax_amount: taxAmount,
        item_total_inclusive_of_tax: itemTotal + taxAmount,
      };
      onLineItemDataChange(updatedData);
    }
  };

  // Toggle whether the entered rate already includes tax
  const handleRateIncludesTaxToggle = (checked: boolean) => {
    const currentRate = editingLineItemData.rate || 0;
    const taxPercentage = editingLineItemData.tax_percentage || 0;
    const quantity = editingLineItemData.quantity || 0;

    if (checked) {
      setRateIncludesTax(true);
      setInclusiveRateSnapshot(currentRate);

      if (isGstEnabled && taxPercentage > 0) {
        const baseRate = roundToTwo(currentRate / (1 + taxPercentage / 100));
        const itemTotal = quantity * baseRate;
        const taxAmount = (itemTotal * taxPercentage) / 100;

        onLineItemDataChange({
          ...editingLineItemData,
          rate: baseRate,
          item_total: itemTotal,
          tax_amount: taxAmount,
          item_total_inclusive_of_tax: itemTotal + taxAmount,
        });
      }
      return;
    }

    const rateToRestore = roundToTwo(
      inclusiveRateSnapshot !== null ? inclusiveRateSnapshot : currentRate,
    );
    const itemTotal = quantity * rateToRestore;
    const taxAmount = isGstEnabled ? (itemTotal * taxPercentage) / 100 : 0;

    setRateIncludesTax(false);
    setInclusiveRateSnapshot(null);

    onLineItemDataChange({
      ...editingLineItemData,
      rate: rateToRestore,
      item_total: itemTotal,
      tax_amount: taxAmount,
      item_total_inclusive_of_tax: isGstEnabled
        ? itemTotal + taxAmount
        : itemTotal,
    });
  };

  // Handle field changes
  const handleFieldChange = (field: string, value: any) => {
    let updatedData = {
      ...editingLineItemData,
      [field]: value,
    };

    // Recalculate item_total and tax_amount when quantity or rate changes
    if (field === "quantity" || field === "rate") {
      const quantity =
        field === "quantity" ? value : editingLineItemData.quantity || 0;
      const rate =
        field === "rate"
          ? roundToTwo(value)
          : roundToTwo(editingLineItemData.rate || 0);
      const itemTotal = quantity * rate;
      const taxPercentage = editingLineItemData.tax_percentage || 0;
      const taxAmount = (itemTotal * taxPercentage) / 100;

      if (rateIncludesTax && isGstEnabled) {
        setInclusiveRateSnapshot(rate * (1 + taxPercentage / 100));
      }

      updatedData = {
        ...updatedData,
        item_total: itemTotal,
        tax_amount: isGstEnabled ? taxAmount : 0,
        item_total_inclusive_of_tax: isGstEnabled
          ? itemTotal + taxAmount
          : itemTotal,
      };
    }

    onLineItemDataChange(updatedData);
  };

  // Validate form for new items
  const isFormValid = () => {
    if (!editingLineItemData) return false;

    const hasDescription = editingLineItemData.description?.trim().length > 0;
    const hasQuantity = editingLineItemData.quantity > 0;
    const hasRate = editingLineItemData.rate > 0;
    const hasAccount = editingLineItemData.account_id?.trim().length > 0;
    // When GST is enabled, overseas requires IGST; domestic allows NO TAX or empty.
    const hasTax = isGstEnabled
      ? isOverseas
        ? editingLineItemData.tax_id?.trim().length > 0
        : editingLineItemData.tax_id?.trim().length > 0 ||
          editingLineItemData.tax_id === "NO_TAX" ||
          (editingLineItemData.tax_id ?? "") === ""
      : true;
    const hasHsn = editingLineItemData.hsn_or_sac?.trim().length > 0;

    return (
      hasDescription && hasQuantity && hasRate && hasAccount && hasTax && hasHsn
    );
  };

  // Handle save with API integration
  const handleSave = async () => {
    if (changeScope === "always" && showAccountChangeSection) {
      // Validate that reason is provided when "Change it always" is selected
      if (!accountChangeReason.trim()) {
        setError("Please provide a reason for the account change");
        return;
      }
      // Call semantic override API first
      await handleSemanticOverride();
    } else {
      // Normal save without API call
      onSave();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  if (!editingLineItemData) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="min-w-[600px] max-w-[700px]">
        {/* Header */}
        <div className="flex w-full items-center justify-between gap-4 p-6 border-b border-solid border-neutral-border">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Edit Line Item
            </span>
            <span className="text-body font-body text-subtext-color">
              Update the details for this line item
            </span>
          </div>
          <IconButton size="small" icon={<FeatherX />} onClick={handleCancel} />
        </div>

        {/* Form Content */}
        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Description *
            </span>
            <TextField className="h-auto w-full flex-none" label="" helpText="">
              <TextField.Input
                placeholder="46B x4R SPB Book Ptg (NS) 70GSM 51 X 76 Cms (1B X 4R ) kgs Batch : Primary Batch"
                value={editingLineItemData.description || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange("description", event.target.value)
                }
              />
            </TextField>
          </div>

          <div className="flex w-full items-start gap-6 flex-1">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Quantity *"
              helpText=""
            >
              <TextField.Input
                ref={quantityInputRef}
                type="number"
                step="any"
                placeholder="0.00"
                value={editingLineItemData.quantity?.toString() || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  // Allow empty string and proper decimal input
                  const quantity = value === "" ? 0 : parseFloat(value) || 0;
                  handleFieldChange("quantity", quantity);
                }}
              />
            </TextField>
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Rate *"
              helpText=""
            >
              <TextField.Input
                ref={rateInputRef}
                type="number"
                step="any"
                placeholder="0.00"
                value={editingLineItemData.rate?.toString() || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  // Allow empty string and proper decimal input
                  const rate = value === "" ? 0 : parseFloat(value) || 0;
                  handleFieldChange("rate", rate);
                }}
              />
            </TextField>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={rateIncludesTax}
              onCheckedChange={(checked) =>
                handleRateIncludesTaxToggle(Boolean(checked))
              }
            />
            <span className="text-caption font-caption text-subtext-color">
              Rate includes tax (strip tax from rate)
            </span>
          </div>

          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex w-full flex-col gap-1">
              <span className="text-caption-bold font-caption-bold text-default-font">
                Account Name *
              </span>
              <AccountNameDropdown
                docType="invoice"
                selectedAccountId={editingLineItemData.account_id}
                selectedAccountName={editingLineItemData.account_name}
                onSelect={handleAccountSelection}
                dropdownClassName="w-[var(--radix-dropdown-menu-trigger-width)] min-w-full"
              />
            </div>
            {showAccountChangeSection && (
              <div className="flex w-full flex-col items-start gap-4 rounded-md bg-brand-100 px-4 py-4">
                {/* Only show the "changed from/to" warning for EXISTING items */}
                {!isNewItem && (
                  <div className="flex w-full items-start gap-3 rounded-md border border-solid border-warning-300 bg-warning-100 px-3 py-3">
                    <FeatherInfo className="text-body font-body text-warning-700" />
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Account name changed
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        From: {storedOriginalAccountName} → To:{" "}
                        {editingLineItemData.account_name}
                      </span>
                    </div>
                  </div>
                )}
                <TextArea
                  className="h-auto w-full flex-none"
                  label={`Reason for change${
                    changeScope === "always" ? " *" : ""
                  }`}
                  helpText={
                    changeScope === "always"
                      ? "Required when applying changes to all future line items"
                      : ""
                  }
                >
                  <TextArea.Input
                    placeholder="Enter reason for changing account name..."
                    value={accountChangeReason}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setAccountChangeReason(event.target.value)
                    }
                  />
                </TextArea>
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    Apply change to <span className="text-error-700">*</span>
                  </span>
                  <div className="flex w-full items-start gap-4">
                    <div
                      className={`flex items-start gap-3 rounded-md px-2 py-2 ${
                        !canOverridePerm
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      } ${
                        changeScope === "always"
                          ? "bg-warning-800"
                          : "border border-solid border-neutral-border bg-default-background"
                      }`}
                      onClick={() => {
                        if (canOverridePerm) {
                          setChangeScope("always");
                        }
                      }}
                    >
                      <div
                        className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 border-solid mt-0.5 ${
                          changeScope === "always"
                            ? "border-white"
                            : "border-neutral-400"
                        }`}
                      >
                        {changeScope === "always" && (
                          <div className="flex h-2 w-2 flex-none items-start rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={`text-body-bold font-body-bold ${
                            changeScope === "always"
                              ? "text-white"
                              : "text-default-font"
                          }`}
                        >
                          Change it always
                        </span>
                        <span
                          className={`text-caption font-caption ${
                            changeScope === "always"
                              ? "text-white"
                              : "text-subtext-color"
                          }`}
                        >
                          Apply this change to all future line items with this
                          description
                        </span>
                      </div>
                    </div>
                    <div
                      className={`flex items-start gap-3 rounded-md px-2 py-2 ${
                        !canOverrideOnce
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      } ${
                        changeScope === "once"
                          ? "bg-warning-800"
                          : "border border-solid border-neutral-border bg-default-background"
                      }`}
                      onClick={() => {
                        if (canOverrideOnce) {
                          setChangeScope("once");
                        }
                      }}
                    >
                      <div
                        className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 border-solid mt-0.5 ${
                          changeScope === "once"
                            ? "border-white"
                            : "border-neutral-400"
                        }`}
                      >
                        {changeScope === "once" && (
                          <div className="flex h-2 w-2 flex-none items-start rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={`text-body-bold font-body-bold ${
                            changeScope === "once"
                              ? "text-white"
                              : "text-default-font"
                          }`}
                        >
                          Change this once
                        </span>
                        <span
                          className={`text-caption font-caption ${
                            changeScope === "once"
                              ? "text-white"
                              : "text-subtext-color"
                          }`}
                        >
                          Apply this change only to this specific line item
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex w-full items-start gap-6 flex-1">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="HSN/SAC Code *"
              helpText="Max 8 characters"
            >
              <TextField.Input
                placeholder="48025790"
                maxLength={8}
                value={editingLineItemData.hsn_or_sac || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange(
                    "hsn_or_sac",
                    event.target.value.slice(0, 8),
                  )
                }
              />
            </TextField>
            {isGstEnabled && (
              <Select
                className="h-auto grow shrink-0 basis-0"
                label="Tax Rate (%) *"
                helpText={
                  isOverseas
                    ? "Select IGST rate for overseas customer"
                    : "Select tax rate"
                }
                value={
                  editingLineItemData.tax_id?.trim()
                    ? editingLineItemData.tax_id
                    : isOverseas
                      ? ""
                      : "NO_TAX"
                }
                onValueChange={handleTaxSelection}
                placeholder={taxesLoading ? "Loading..." : "Select tax rate"}
                disabled={taxesLoading}
              >
                {!isOverseas && <Select.Item value="NO_TAX">NO TAX</Select.Item>}
                {taxes.map((tax) => (
                  <Select.Item key={tax.tax_id} value={tax.tax_id}>
                    {tax.tax_name} ({tax.tax_percentage}%)
                  </Select.Item>
                ))}
              </Select>
            )}
          </div>

          <div className="flex w-full items-center justify-between rounded-md bg-success-50 px-4 py-3">
            <span className="text-body-bold font-body-bold text-default-font">
              Item Total {isGstEnabled ? "(including tax)" : ""}
            </span>
            <span className="text-heading-3 font-heading-3 text-success-600">
              {currencySymbol}
              {(() => {
                const subtotal = editingLineItemData.item_total || 0;
                const taxAmount =
                  (subtotal * (editingLineItemData.tax_percentage || 0)) / 100;
                return isGstEnabled
                  ? roundOff(subtotal + taxAmount)
                  : roundOff(subtotal);
              })()}
            </span>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex w-full flex-col items-start gap-2 px-6">
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-error-300 bg-error-100 px-3 py-3">
              <FeatherInfo className="text-body font-body text-error-700" />
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <span className="text-body-bold font-body-bold text-error-700">
                  Error
                </span>
                <span className="text-body font-body text-error-700">
                  {error}
                </span>
              </div>
            </div>
          </div>
        )}
        {/* Footer */}
        <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <Button
            variant="neutral-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="brand-primary"
            onClick={handleSave}
            disabled={
              isLoading ||
              (isNewItem && !isFormValid()) ||
              (showAccountChangeSection && changeScope === null) ||
              (changeScope === "always" &&
                showAccountChangeSection &&
                !accountChangeReason.trim())
            }
          >
            {isLoading
              ? isNewItem
                ? "Adding..."
                : "Saving..."
              : isNewItem
                ? "Add Item"
                : "Save Changes"}
          </Button>
        </div>
      </Dialog.Content>

      {/* Review Request Modal */}
      {invoiceId && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.INVOICE}
          actionCode={ACTION_CODES.OVERRIDE_GL_PERMANENT}
          entityType="invoice"
          entityId={
            typeof invoiceId === "string" ? parseInt(invoiceId) : invoiceId
          }
          changeDetails={pendingOverrideAction?.changeDetails}
          onSuccess={async () => {
            // Review request submitted successfully
            // The review will be pending until approved by a reviewer
            // No need to check review status here - it will be pending, not approved
            // The override will be applied once the review is approved (via ApprovedChangesModal)
          }}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            setError(
              error instanceof Error
                ? error.message
                : "Failed to create review request",
            );
          }}
        />
      )}
    </Dialog>
  );
};

export default EditLineItemForInvoiceComponent;
